import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  MultiStepFormComponent,
  Step,
  StepNavigatedEvent,
} from '../../../../components/multi-step-form/multi-step-form.component';
import { ProjectDataStepComponent } from './steps/project-data-step/project-data-step.component';
import { ProjectRolesStepComponent } from './steps/project-roles-step/project-roles-step.component';
import { ProjectScheduleStepComponent } from './steps/project-schedule-step/project-schedule-step.component';
import { DxFormModule, DxTextBoxModule, DxSelectBoxModule, DxValidatorModule } from 'devextreme-angular';
import { GridConnectionService } from '../../../../services/grid-connection.service';
import { DashboardService } from '../../../../services/dashboard.service';
import { ProjectService } from '../../../../services/project.service';
import { AppGlobals } from '../../../../app.globals';
import { TemplatesService } from 'src/app/services/templates.service';
import { PhasesService } from 'src/app/services/phases.service';
import { DataAccessService } from 'src/app/services/data-access.service';
import { ConfigService } from 'src/app/services/config.service';
import { ScheduleService } from 'src/app/services/schedule-item.service';
import { Subscription } from 'rxjs';
import {
  ConfigScheduleItem,
  GanttDependencyType,
} from 'src/app/pages/config/project-config/config-schedule/config-schedule.types';
import { DateFormatter } from 'src/app/helpers/date-formatter';
import { ContactsService } from 'src/app/services/contacts.service';
import { ContractSelectorService } from 'src/app/services/contracts/contract-selector.service';
import { Contract } from 'src/app/pages/contracts/contract-form/contract-form.component';
import { ProjectConnectionStepComponent } from './steps/project-connection-step/project-connection-step.component';

export interface DepartmentRole {
  departmentId: number;
  departmentName: string;
  userMail: string | null;
  /** #4994: the role is held by a whole department instead of one person. */
  assignedDepartmentId: number | null;
}

export interface Schedule {
  id: number;
  parentId: number | null;
  title: string;
  itemType: string;
  startDate: Date;
  endDate: Date;
  dependencyOnId?: number | null;
  dependencyType?: any;
  progress?: number;
  sortingIndex: number;
}

export interface ProjectData {
  nr: string;
  name: string;
  partner: { id: number; contactType: string; } | null;
  epc: { id: number; contactType: string;} | null;
  areaTypeId: number;
  startDate: Date;
  roles: DepartmentRole[];
  schedule?: Schedule[];
}

@Component({
    selector: 'app-project-creation-form',
    imports: [
        CommonModule,
        MultiStepFormComponent,
        ProjectDataStepComponent,
        ProjectRolesStepComponent,
        ProjectScheduleStepComponent,
        ProjectConnectionStepComponent,
        DxFormModule,
        DxTextBoxModule,
        DxSelectBoxModule,
        DxValidatorModule,
    ],
    templateUrl: './project-creation-form.component.html',
    styleUrls: ['./project-creation-form.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectCreationFormComponent implements OnInit, OnDestroy {
  steps: Step[] = [];
  finishText = $localize`:@@createProject:Create Project`;

  // Input properties (can be provided via Input or route params)
  gridConnectionId: any;
  gridConnectionTitle: string = '';
  projects: any[] = []; // For project number generation

  // Flag to determine if component is used as standalone page
  private isStandalonePage: boolean = false;

  projectData: ProjectData = {
    nr: null,
    name: '',
    partner: null,
    epc: null,
    areaTypeId: null,
    startDate: new Date((new Date()).setHours(0, 0, 0, 0)),
    roles: [],
  };

  // Reference data
  persons: any[] = [];
  companies: any[] = [];
  areaTypes: any[] = [];
  departments: any[] = [];
  users: any[] = [];
  scheduleItems: ConfigScheduleItem[] = [];
  schedule: Schedule[] = [];

  // Contract linking (final step) — only shown when the site already has contracts
  siteContracts: Contract[] = [];
  contractTypes: any[] = [];
  selectedContractIds: number[] = [];
  hasSiteContracts: boolean = false;
  private viewReady: boolean = false;
  private siteContractsLoaded: boolean = false;

  areaTypeChanged: boolean = false;

  @ViewChild('projectDataTemplate')
  projectDataTemplate!: TemplateRef<any>;
  @ViewChild('projectRolesTemplate')
  projectRolesTemplate!: TemplateRef<any>;
  @ViewChild('projectScheduleTemplate')
  projectScheduleTemplate!: TemplateRef<any>;
  @ViewChild('projectConnectionTemplate')
  projectConnectionTemplate!: TemplateRef<any>;
  @ViewChild(MultiStepFormComponent)
  multiStepForm!: MultiStepFormComponent;

  @ViewChild('scheduleStep')
  scheduleStep!: ProjectScheduleStepComponent;

  userListSubscription: Subscription;
  private contractSubscriptions: Subscription[] = [];
  private dateFormatter = new DateFormatter();

  constructor(
    private cdr: ChangeDetectorRef,
    private gridConnectionService: GridConnectionService,
    private contactsService: ContactsService,
    private contractSelectorService: ContractSelectorService,
    private projectService: ProjectService,
    private templatesService: TemplatesService,
    private phasesService: PhasesService,
    private dataAccessService: DataAccessService,
    private configService: ConfigService,
    private scheduleService: ScheduleService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {}

  async ngOnInit() {
    // Check if being used as standalone page via routing
    this.isStandalonePage = true;
    this.gridConnectionId = this.activatedRoute.parent?.snapshot.params.id;

    await this.loadReferenceData();
    this.initializeFormData();
  }

  ngAfterViewInit() {
    this.viewReady = true;
    this.buildSteps();
  }

  ngOnDestroy() {
    this.userListSubscription?.unsubscribe();
    this.contractSubscriptions.forEach(sub => sub.unsubscribe());
  }

  // Builds the wizard steps once both the view (template refs) and the site-contract
  // data are ready. The contract step is appended only when the site has contracts.
  private buildSteps() {
    if (!this.viewReady || !this.siteContractsLoaded) {
      return;
    }

    const steps: Step[] = [
      {
        id: 'project-data',
        title: $localize`:@@projectData:Project Data`,
        icon: 'files',
        template: this.projectDataTemplate,
        description: $localize`:@@projectDataDescription:Enter the basic project information.`,
      },
      {
        id: 'project-roles',
        title: $localize`:@@projectRoles:Project Roles`,
        icon: 'user',
        template: this.projectRolesTemplate,
        description: $localize`:@@projectRolesDescription:Assign users to department roles for this project.`,
      },
      {
        id: 'project-schedule',
        title: $localize`:@@projectSchedule:Project Schedule`,
        icon: 'tasks',
        template: this.projectScheduleTemplate,
        description: $localize`:@@projectScheduleDescription:Review the project timeline based on your start date.`,
      },
    ];

    if (this.hasSiteContracts) {
      steps.push({
        id: 'project-connection',
        title: $localize`:@@contract:Contract`,
        icon: 'link',
        template: this.projectConnectionTemplate,
        description: $localize`:@@linkContractProjectSub:Select the contracts you would like to link to the new project.`,
        isValid: true,
      });
    }

    this.steps = steps;

    // Manually trigger change detection since we're using OnPush
    this.cdr.detectChanges();
  }

  private async loadReferenceData() {
    try {
      const personPromise = this.getPersons();
      const companyPromise = this.getCompanies();
      const areaPromise = this.getAreaTypes();
      const depPromise = this.getDepartments();
      const userPromise = this.subscribeToUserList();
      const gridDataPromise = this.loadGridConnectionData();
      const siteContractsPromise = this.loadSiteContracts();

      await Promise.all([personPromise, companyPromise, areaPromise, depPromise, userPromise, gridDataPromise, siteContractsPromise]);

      this.cdr.detectChanges();
    } catch (error) {
      AppGlobals.ShowErrorMessage('Error loading reference data: ' + error);
    }
  }

  private async loadGridConnectionData() {
    try {
      // Load grid connection title
      const titlePromise = this.gridConnectionService.getGridConnectionTitle({
        GridConnectionId: this.gridConnectionId,
      });

      const projectsPromise = this.gridConnectionService.getProjectsByGridConnectionId({
        requestItem: { GridConnectionId: this.gridConnectionId },
      });

      const [titleResult, projectsResult] = await Promise.all([titlePromise, projectsPromise]);

      if (titleResult.hasError === true) {
        AppGlobals.ShowErrorMessage(titleResult.feedback);
      } else {
        this.gridConnectionTitle = titleResult.gridConnectionTitleItem.title;
      }

      if (projectsResult.hasError === true) {
        AppGlobals.ShowErrorMessage(projectsResult.feedback);
      } else {
        this.projects = projectsResult.projectCardItems;
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage('Error loading grid connection data: ' + error);
    }
  }

  async getPersons() {
    try {
      const result = await this.contactsService.getPersons({});
      if (!result.hasError) {
        this.persons = result.personItems;
      } else {
        AppGlobals.ShowErrorMessage(result.feedback);
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage('Error loading reference data: ' + error);
    }
  }

  async getCompanies() {
    try {
      const result = await this.contactsService.getCompanies({});
      if (!result.hasError) {
        this.companies = result.companyItems;
      } else {
        AppGlobals.ShowErrorMessage(result.feedback);
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage('Error loading reference data: ' + error);
    }
  }

  async getAreaTypes() {
    try {
      const result = await this.templatesService.getAreaTypes();
      if (!result.hasError) {
        this.areaTypes = result.areaTypes;
      } else {
        AppGlobals.ShowErrorMessage(result.feedback);
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage('Error loading reference data: ' + error);
    }
  }

  async getDepartments() {
    try {
      const result = await this.dataAccessService.getDepartments({});
      if (!result.hasError) {
        this.departments = result.departmentItems;
      } else {
        AppGlobals.ShowErrorMessage(result.feedback);
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage('Error loading departments: ' + error);
    }
  }

  private async loadSiteContracts(): Promise<void> {
    this.contractSubscriptions.push(
      this.contractSelectorService.siteContracts$.subscribe(contracts => {
        this.siteContracts = contracts ?? [];
      })
    );
    this.contractSubscriptions.push(
      this.contractSelectorService.contractTypes$.subscribe(contractTypes => {
        this.contractTypes = contractTypes ?? [];
      })
    );

    await Promise.all([
      this.contractSelectorService.getProjectContractsBySite({ SiteId: this.gridConnectionId }),
      this.contractSelectorService.getContractTypes({}),
    ]);

    this.hasSiteContracts = this.siteContracts.length > 0;
    this.siteContractsLoaded = true;
    this.buildSteps();
  }

  private subscribeToUserList(): void {
    this.userListSubscription = this.configService.userList$.subscribe(users => {
      this.users = users;
    });

    // Trigger the initial load if the user list is empty
    if (this.users.length === 0) {
      this.configService.getListOfUsers({});
    }
  }

  private initializeFormData() {
    // Reset form data similar to resetFormValues in original component
    this.projectData = {
      nr: null,
      name: this.gridConnectionTitle,
      partner: null,
      epc: null,
      areaTypeId: this.areaTypes.at(0).id,
      startDate: new Date((new Date()).setHours(0, 0, 0, 0)),
      roles: this.initializeRoles(),
    };

    this.generateProjectNr();

    // Load initial schedule data if area type is set
    if (this.projectData.areaTypeId) {
      this.loadScheduleData(this.projectData.areaTypeId);
    }
  }

  private initializeRoles(): DepartmentRole[] {
    return this.departments.map(dept => ({
      departmentId: dept.id,
      departmentName: dept.name,
      userMail: null,
      assignedDepartmentId: null,
    }));
  }

  private generateProjectNr() {
    // Use the same logic as the original popup form
    if (!this.projects || this.projects.length === 0) {
      this.projectData.nr = '1';
      this.projectData.name = this.gridConnectionTitle + ' 1';
      this.cdr.detectChanges();
      return;
    }

    const existingNumbers = this.projects.map(p => {
      const nr = p.nr.split('-');
      return parseInt(nr[nr.length - 1]);
    });

    existingNumbers.sort((a, b) => a - b);

    let newNr = 1;

    for (let i = 0; i < existingNumbers.length; i++) {
      if (existingNumbers[i] !== newNr) {
        break; // Found a gap in the sequence
      }
      newNr++;
    }

    this.projectData.nr = newNr.toString();
    this.projectData.name = this.gridConnectionTitle + ' ' + newNr;
    this.cdr.detectChanges();
  }

  onProjectDataChange = (updatedProjectData: ProjectData) => {
    const previousAreaTypeId = this.projectData.areaTypeId;

    // Preserve existing schedule if they exist and area type hasn't changed
    if (previousAreaTypeId === updatedProjectData.areaTypeId && this.projectData.schedule) {
      updatedProjectData.schedule = this.projectData.schedule;
    }

    this.projectData = { ...this.projectData, ...updatedProjectData };

    // Only reload schedule if area type actually changed
    if (previousAreaTypeId !== updatedProjectData.areaTypeId && updatedProjectData.areaTypeId) {
      this.loadScheduleData(updatedProjectData.areaTypeId);
    }

    this.cdr.detectChanges();
  };

  private async loadScheduleData(areaTypeId: number) {
    try {
      const result = await this.scheduleService.getScheduleItemConfig(areaTypeId);
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        this.scheduleItems = [];
        this.schedule = [];
        this.projectData.schedule = [];
      } else {
        this.scheduleItems = result.scheduleItems;
        this.schedule = this.convertConfigToSchedule(result.scheduleItems, this.projectData.startDate);
        this.projectData.schedule = this.schedule;
      }
    } catch (error) {
      AppGlobals.ShowErrorMessage('Error loading schedule data: ' + error);
      this.scheduleItems = [];
      this.schedule = [];
      this.projectData.schedule = [];
    }
  }

  private convertConfigToSchedule(configItems: ConfigScheduleItem[], projectStartDate: Date): Schedule[] {
    console.log( 'convertConfigToSchedule',configItems);

    const relitiveTasks = new Map<number, { start: number; end: number,realstart: number, realend: number }>();

    // Calculate relative dates using the same logic as the schedule step component
    // using real values as when completeOnCreation is true, the task is set to teh project start but the dependency is not effected only the task and its parents
    const mapGanttTaskRecursive = (id: number | null): { start: number; end: number, realstart: number, realend: number } => {
      const item = this.scheduleItems.find(item => item.id === id);
      const children = this.scheduleItems.filter(s => s.parentId === id);
      let start = item?.startOffset ?? 0;
      let end = item?.startOffset ?? 0;

      if (children.length > 0) {
        for (const child of children) {
          let childStart;
          let childEnd;
          if (!relitiveTasks.has(child.id)) {
            const res = mapGanttTaskRecursive(child.id);
            childStart = res.realstart;
            childEnd = res.realend;
          } else {
            const c = relitiveTasks.get(child.id);
            childStart = c.realstart;
            childEnd = c.realend;
          }

          if (childStart < start) {
            start = childStart;
          }
          if (childEnd > end) {
            end = childEnd;
          }
        }
      } else if (item != null) {
        if (item?.dependencyOnId !== null && item?.dependencyOnId !== undefined) {
          if (!relitiveTasks.has(item.dependencyOnId)) {
            mapGanttTaskRecursive(item.dependencyOnId);
          }
          const dependancy = relitiveTasks.get(item.dependencyOnId);

          switch (item.dependencyType) {
            case GanttDependencyType.FF:
            case GanttDependencyType.FS:
              start += dependancy.end;
              end += dependancy.end;
              break;
            case GanttDependencyType.SS:
            case GanttDependencyType.SF:
              start += dependancy.start;
              end += dependancy.start;
              break;
          }
          switch (item.dependencyType) {
            case GanttDependencyType.SS:
            case GanttDependencyType.FS:
              end += item.duration ?? 0;
              break;
            case GanttDependencyType.SF:
            case GanttDependencyType.FF:
              start -= item.duration ?? 0;
              break;
          }
        } else {
          end += item.duration;
        }
      }
      if (item) {
        relitiveTasks.set(id, {
          start: start,
          end: end,
          realstart: item?.completedOnCreation ? 0 - item.duration : start,
          realend: item?.completedOnCreation ? 0 : end,
        });
      }
      return { start, end, realstart: item?.completedOnCreation ? 0 - item.duration : start, realend: item?.completedOnCreation ? 0 : end };
    };

    // Calculate all relative dates
    mapGanttTaskRecursive(null);

    // Convert to actual dates
    return configItems.map(item => {
      const relativeTask = relitiveTasks.get(item.id);
      let start = relativeTask.realstart;
      let end = relativeTask.realend;
      return {
        id: item.id,
        parentId: item.parentId,
        title: item.title,
        itemType: item.itemType,
        startDate: new Date(
          projectStartDate.getFullYear(),
          projectStartDate.getMonth(),
          projectStartDate.getDate() + start,
          0,
          0,
          0,
          0
        ),
        endDate: new Date(
          projectStartDate.getFullYear(),
          projectStartDate.getMonth(),
          projectStartDate.getDate() + end,
          0,
          0,
          0,
          0
        ),
        dependencyOnId: item.completedOnCreation ? null : item.dependencyOnId,
        dependencyType: item.completedOnCreation ? null : item.dependencyType,
        progress: item.completedOnCreation ? 100 : 0,
        sortingIndex: item.sortingIndex,
      };
    });
  }

  // Method to update schedule when user customizes them
  onScheduleChange = (updatedSchedule: Schedule[]) => {
    this.schedule = updatedSchedule;
    this.projectData.schedule = updatedSchedule;
    this.cdr.detectChanges();
  };

  // Public method to reset the form (called when popup opens)
  public resetForm() {
    this.initializeFormData();
  }

  onCancelClicked() {
    this.router.navigate([`/site/${this.gridConnectionId}/site-overview`]);
  }

  async onFinishClicked() {
    try {
      // Use the same payload structure as the original popup form and convert dates to dateOnly format
      const payload = {
        gridConnectionId: this.gridConnectionId,
        projectNr: this.projectData.nr + '',
        projectTitle: this.projectData.name,
        partner: this.projectData.partner,
        epc: this.projectData.epc,
        areaTypeId: this.projectData.areaTypeId,
        startDate: this.dateFormatter.toDateOnly(this.projectData.startDate),
        roles: this.projectData.roles.map(r => ({
          departmentId: r.departmentId,
          userMail: r.userMail,
          assignedDepartmentId: r.assignedDepartmentId,
        })),
        schedule: this.schedule.map(s => ({
          configId: s.id,
          startDate: this.dateFormatter.toDateOnly(s.startDate),
          endDate: this.dateFormatter.toDateOnly(s.endDate),
          dependencyOnId: s.dependencyOnId,
          dependency: s.dependencyType,
        })),
      };

      const result = await this.projectService.createNewProject({ ProjectItem: payload });

      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }

      if (this.selectedContractIds.length > 0 && result.projectId) {
        await this.contractSelectorService.addNewProjectToContracts({
          ProjectId: result.projectId,
          ContractIds: this.selectedContractIds,
        });
      }

      this.router.navigate([`/site/${this.gridConnectionId}/site-overview`]);
    } catch (error) {
      AppGlobals.ShowErrorMessage('Error creating project: ' + error);
    }
  }

  onAreaTypesChange() {
    this.loadScheduleData(this.projectData.areaTypeId);
    this.areaTypeChanged = true;
    this.cdr.detectChanges();
  }

  onStartDateChange(value: Date) {
    if (!this.schedule) {
      return;
    }
    this.projectData.startDate = new Date(value.setHours(0, 0, 0, 0));

    this.schedule = this.convertConfigToSchedule(this.scheduleItems, value);
    this.scheduleStep?.calculateDatesAndMapTasks();
    this.cdr.detectChanges();
  }

  onNextClicked(e: StepNavigatedEvent) {
    if (e.toStep?.id === 'project-schedule' && this.scheduleStep) {
      this.scheduleStep?.calculateDatesAndMapTasks();
      this.cdr.detectChanges();
    }
  }
}
